import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Box, Skeleton} from "@mui/material";
import LookupProductCard from "../../../../../../components/app-specific/lookup-product-card";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import useQuery from "../../../../../../hooks/use-query";
import {stringifyUrl} from "query-string";
import ElementIds from "../../../../../../../core/models/element-ids";
import Utils from "../../../../../../../core/services/utils/utils";

const LookupProductInformationViewRelatedPartsSection = ({
                                                             className,
                                                             id,
                                                             partNumber,
                                                             removeSection,
                                                             addSection,
                                                             shown,
                                                             idToListenTo,
                                                         }) => {
    const [query] = useQuery();
    const [loading, setLoading] = useState(true);
    const [relations, setRelations] = useState([]);
    const isMounted = useIsMounted();
    const scrollerRef = useRef(false);

    /**
     * Fetches the products based on the given search parameters.
     * @type {function(*, *): string}
     */
    const getProductNavigationLink = useCallback(((partNo, codes = undefined) => stringifyUrl({
        url: routeFunctions.lookup.single(partNo),
        query: {
            customer: query.customer,
            ...(codes ? {codes: codes} : {})
        }
    }, {arrayFormat: 'bracket'})), [query.customer])

    const relationsParsed = useMemo(() => {
        return relations.map(e => ({
            relationTitle: e.relationTitle ?? "Section",
            products: e.partsList.map((part) => ({
                info: {
                    id: part.id,
                    inCompareList: part.isInCompareList,
                    images: part.cover?.url ? [part.cover?.url] : [],
                    codes: [],
                    partNo: part.partNumber,
                    rootCategoryName: "",
                    leafCategoryName: part.category?.title
                },
                link: {
                    href: getProductNavigationLink(part.partNumber),
                    replace: false,
                }
            }))
        }))
    }, [getProductNavigationLink, relations])

    /**
     * Calls the api for getting the related parts of a part. Does nothing if component is unmounted or api response
     * is false or no data is provided
     */
    const getRelatedParts = useCallback(async () => {
        scrollerRef.current = true;
        setLoading(true);
        const response = await Api.getPartRelatedParts(partNumber);
        if (!isMounted())
            return;
        setLoading(false);
        const items = response.data ?? [];
        if (response?.resultFlag && items.length) {
            const groupedByRelationJustInCase = Object.values(items.reduce((acc, item) => {
                acc[item.relationTitle] = {
                    relationTitle: item.relationTitle,
                    partsList: [...acc[item.relationTitle]?.partsList ?? [], ...item.partsList],
                };
                return acc;
            }, {}))
            setRelations(groupedByRelationJustInCase);
        } else {
            removeSection(id);
        }
    }, [id, isMounted, partNumber, removeSection])

    /**
     * As soon as the component mounts:
     * - attaches an event listener to window's scrolling effect that will determine if the extra information of the
     * a part needs to be fetched or not.
     */
    useEffect(() => {
        addSection(id);
        const element = document.getElementById(idToListenTo);
        if (Utils.isScrolledIntoView(element, true)) {
            void getRelatedParts();
        } else {
            scrollerRef.current = false;
            const onContainerScrolled = () => {
                if (scrollerRef.current)
                    return;
                const element = document.getElementById(idToListenTo);
                if (!element)
                    return;
                if (!Utils.isScrolledIntoView(element))
                    return;
                void getRelatedParts();
            }
            const main = document.getElementById(ElementIds.panel.main);
            main.addEventListener('scroll', onContainerScrolled)
            return () => window.removeEventListener('scroll', onContainerScrolled)
        }
    }, [addSection, getRelatedParts, id, idToListenTo])

    if (!shown) {
        return (
            <Row id={id} className={`no-gutters w-100`}/>
        );
    }

    return (
        <>
            <Row id={id} className={`${className} no-gutters w-100`}>
                {
                    (loading) &&
                    <>
                        <Box sx={{
                            width: '100%',
                            maxHeight: 380,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'top',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            display: 'flex',
                            paddingBlock: 2,
                        }}>
                            {
                                Array.from({length: 5}).map((_, index) => (
                                    <Box key={`loading-${index}`} component={'span'}
                                         sx={{minWidth: 282, paddingInline: 2}}>
                                        <Skeleton width={250} height={330}/>
                                    </Box>
                                ))
                            }
                        </Box>
                    </>
                }
                {
                    !(loading) &&
                    relationsParsed?.map((e) => (
                        <Row key={e.relationTitle} className='align-items-center justify-content-start no-gutters w-100 mx-2 mb-3'>
                            <Col xs={12} className={'mb-2'}>
                                <p className='font-weight-semi-bold text-md text-color-1'>
                                    {e?.relationTitle ?? 'Section'}
                                </p>
                            </Col>
                            <Col xs={12}>
                                <Box sx={{
                                    width: '100%',
                                    maxHeight: 380,
                                    whiteSpace: 'nowrap',
                                    verticalAlign: 'top',
                                    overflowX: 'auto',
                                    overflowY: 'hidden',
                                    display: 'flex',
                                    paddingBlock: 2,
                                }}>
                                    {
                                        e?.products.map(product => (
                                            <Box key={product.info.id} component={'span'} sx={{minWidth: 250, paddingInline: 2}}>
                                                <LookupProductCard
                                                    info={product.info}
                                                    priceInfo={product.priceInfo}
                                                    availabilityInfo={product.availabilityInfo}
                                                    link={product.link}
                                                />
                                            </Box>
                                        ))
                                    }
                                </Box>
                            </Col>
                        </Row>
                    ))
                }
            </Row>
        </>
    );

}

export default LookupProductInformationViewRelatedPartsSection;
